import React, {
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";

const ExamContext = createContext(null);

const STORAGE_KEY = "oes_session";

/* ==========================================================
   LOAD SESSION
   ========================================================== */

function loadSession() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);

    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw);

    if (!parsed || typeof parsed !== "object") {
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
}

/* ==========================================================
   PROVIDER
   ========================================================== */

export function ExamProvider({ children }) {
  const saved = loadSession();

  /* ========================================================
     USER
     ======================================================== */

  const [currentUser, setCurrentUser] = useState(
    saved?.currentUser || null
  );

  /* ========================================================
     SELECTED COURSE
     ======================================================== */

  const [selectedCourse, setSelectedCourseState] = useState(
    saved?.selectedCourse || null
  );

  /* ========================================================
     SELECTED EXAM
     ======================================================== */

  const [selectedExam, setSelectedExamState] = useState(
    saved?.selectedExam || null
  );

  /* ========================================================
     EXAM-SPECIFIC STATE
     ======================================================== */

  const [registrationId, setRegistrationId] = useState(
    saved?.registrationId || null
  );

  const [criteriaAccepted, setCriteriaAccepted] = useState(
    saved?.criteriaAccepted || false
  );

  const [paymentStatus, setPaymentStatus] = useState(
    saved?.paymentStatus || "Pending"
  );

  const [schedule, setSchedule] = useState(
    saved?.schedule || null
  );

  const [attemptId, setAttemptId] = useState(
    saved?.attemptId || null
  );

  const [attempt, setAttempt] = useState(
    saved?.attempt || null
  );

  const [lastResult, setLastResult] = useState(
    saved?.lastResult || null
  );

  /* ========================================================
     RESET EXAM STATE
     ======================================================== */

  /*
   * Clears only the currently stored exam-flow state.
   *
   * It does NOT affect the logged-in user.
   *
   * IMPORTANT:
   * Backend remains the source of truth for:
   * - registration
   * - payment
   * - schedule
   * - attempt
   * - result
   */

  const resetExamState = () => {
    setRegistrationId(null);
    setCriteriaAccepted(false);
    setPaymentStatus("Pending");
    setSchedule(null);
    setAttemptId(null);
    setAttempt(null);
    setLastResult(null);
  };

  /* ========================================================
     SELECT COURSE
     ======================================================== */

  const selectCourse = (course) => {
    const oldCourseId = selectedCourse?.id;
    const newCourseId = course?.id;

    /*
     * If the student moves to another course,
     * clear the previous exam-flow state.
     */

    if (
      oldCourseId != null &&
      newCourseId != null &&
      Number(oldCourseId) !== Number(newCourseId)
    ) {
      resetExamState();
      setSelectedExamState(null);
    }

    setSelectedCourseState(course);
  };

  /* ========================================================
     SELECT EXAM
     ======================================================== */

  const selectExam = (exam) => {
    const oldExamId = selectedExam?.id;
    const newExamId = exam?.id;

    /*
     * Same exam:
     *
     * Keep the existing flow state.
     *
     * Example:
     * Student is already inside CMA Part 1
     * and a component updates the exam object.
     */

    if (
      oldExamId != null &&
      newExamId != null &&
      Number(oldExamId) === Number(newExamId)
    ) {
      setSelectedExamState(exam);
      return;
    }

    /*
     * Different exam:
     *
     * NEVER carry payment/registration/schedule
     * information from the previous exam.
     */

    resetExamState();

    setSelectedExamState(exam);
  };

  /* ========================================================
     PERSIST SESSION
     ======================================================== */

  useEffect(() => {
    /*
     * No logged-in user:
     * completely clear the session.
     */

    if (!currentUser) {
      localStorage.removeItem(STORAGE_KEY);
      return;
    }

    const session = {
      currentUser,

      selectedCourse,

      selectedExam,

      registrationId,

      criteriaAccepted,

      paymentStatus,

      schedule,

      attemptId,

      attempt,

      lastResult,
    };

    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify(session)
    );
  }, [
    currentUser,
    selectedCourse,
    selectedExam,
    registrationId,
    criteriaAccepted,
    paymentStatus,
    schedule,
    attemptId,
    attempt,
    lastResult,
  ]);

  /* ========================================================
     LOGOUT
     ======================================================== */

  const logout = () => {
    setCurrentUser(null);

    setSelectedCourseState(null);

    setSelectedExamState(null);

    setRegistrationId(null);

    setCriteriaAccepted(false);

    setPaymentStatus("Pending");

    setSchedule(null);

    setAttemptId(null);

    setAttempt(null);

    setLastResult(null);

    localStorage.removeItem(STORAGE_KEY);

    localStorage.removeItem("access_token");

    localStorage.removeItem("refresh_token");
  };

  /* ========================================================
     START NEW EXAM FLOW
     ======================================================== */

  const startNewExamFlow = () => {
    resetExamState();

    setSelectedCourseState(null);

    setSelectedExamState(null);
  };

  /* ========================================================
     CONTEXT VALUE
     ======================================================== */

  const value = {
    /* USER */
    currentUser,
    setCurrentUser,

    /* COURSE */
    selectedCourse,
    setSelectedCourse: selectCourse,
    selectCourse,

    /* EXAM */
    selectedExam,
    setSelectedExam: selectExam,
    selectExam,

    /* REGISTRATION */
    registrationId,
    setRegistrationId,

    /* CRITERIA */
    criteriaAccepted,
    setCriteriaAccepted,

    /* PAYMENT */
    paymentStatus,
    setPaymentStatus,

    /* SCHEDULE */
    schedule,
    setSchedule,

    /* ATTEMPT */
    attemptId,
    setAttemptId,

    attempt,
    setAttempt,

    /* RESULT */
    lastResult,
    setLastResult,

    /* ACTIONS */
    resetExamState,
    logout,
    startNewExamFlow,
  };

  return (
    <ExamContext.Provider value={value}>
      {children}
    </ExamContext.Provider>
  );
}

/* ==========================================================
   HOOK
   ========================================================== */

export function useExam() {
  const ctx = useContext(ExamContext);

  if (!ctx) {
    throw new Error(
      "useExam must be used inside <ExamProvider>"
    );
  }

  return ctx;
}